import React, { useState, useEffect, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { HexColorPicker } from 'react-colorful';
import { 
  Form, 
  Button, 
  Card, 
  Typography, 
  Space, 
  Alert,
  Divider,
  Badge,
  Tooltip
} from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import {
  createOrUpdateProfile,
  getProfile,
} from '../../features/profile/profileSlice';
import {
  EditOutlined,
  SaveOutlined,
  UserOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;

const ProfileComp = () => {
  const { profile, loading, error } = useSelector(
    (state) => state.profile
  );
  const dispatch = useDispatch();
  const [text, setText] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [wordCount, setWordCount] = useState(0);
  const [success, setSuccess] = useState(false);
  const [failed, setFailed] = useState(false);
  const [showTextColorPicker, setShowTextColorPicker] = useState(false);
  const [showBackgroundColorPicker, setShowBackgroundColorPicker] = useState(false);
  const [textColor, setTextColor] = useState('#000000');
  const [backgroundColor, setBackgroundColor] = useState('#ffff00');
  const [textColorHex, setTextColorHex] = useState('#000000');
  const [backgroundColorHex, setBackgroundColorHex] = useState('#ffff00');
  const quillRef = useRef(null);
  const textColorRef = useRef(null);
  const backgroundColorRef = useRef(null);

  // Close color pickers when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (textColorRef.current && !textColorRef.current.contains(event.target)) {
        setShowTextColorPicker(false);
      }
      if (backgroundColorRef.current && !backgroundColorRef.current.contains(event.target)) {
        setShowBackgroundColorPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Apply text color
  const applyTextColor = (color) => {
    const quill = quillRef.current?.getEditor();
    if (quill) {
      const range = quill.getSelection(true); // Force focus to get current selection
      if (range) {
        if (range.length === 0) {
          // No selection - set format for future typing
          quill.format('color', color);
        } else {
          // Has selection - apply to selected text
          quill.formatText(range.index, range.length, 'color', color);
          // Maintain selection after formatting
          quill.setSelection(range.index, range.length);
        }
      } else {
        // No range, just set the format
        quill.format('color', color);
      }
    }
  };

  // Apply background color
  const applyBackgroundColor = (color) => {
    const quill = quillRef.current?.getEditor();
    if (quill) {
      const range = quill.getSelection(true); // Force focus to get current selection
      if (range) {
        if (range.length === 0) {
          // No selection - set format for future typing
          quill.format('background', color);
        } else {
          // Has selection - apply to selected text
          quill.formatText(range.index, range.length, 'background', color);
          // Maintain selection after formatting
          quill.setSelection(range.index, range.length);
        }
      } else {
        // No range, just set the format
        quill.format('background', color);
      }
    }
  };

  // Handle text color change
  const handleTextColorChange = (color) => {
    setTextColor(color);
    setTextColorHex(color);
    applyTextColor(color);
  };

  // Handle background color change
  const handleBackgroundColorChange = (color) => {
    setBackgroundColor(color);
    setBackgroundColorHex(color);
    applyBackgroundColor(color);
  };

  // Handle hex input for text color
  const handleTextColorHexChange = (e) => {
    const value = e.target.value;
    setTextColorHex(value);
    if (/^#[0-9A-F]{6}$/i.test(value)) {
      setTextColor(value);
      applyTextColor(value);
    }
  };

  // Handle hex input for background color
  const handleBackgroundColorHexChange = (e) => {
    const value = e.target.value;
    setBackgroundColorHex(value);
    if (/^#[0-9A-F]{6}$/i.test(value)) {
      setBackgroundColor(value);
      applyBackgroundColor(value);
    }
  };

  // Handle hex input key press
  const handleHexKeyPress = (e, isTextColor) => {
    if (e.key === 'Enter') {
      const value = e.target.value;
      if (/^#[0-9A-F]{6}$/i.test(value)) {
        if (isTextColor) {
          setTextColor(value);
          applyTextColor(value);
        } else {
          setBackgroundColor(value);
          applyBackgroundColor(value);
        }
      } else {
        // Reset to current color if invalid
        if (isTextColor) {
          setTextColorHex(textColor);
        } else {
          setBackgroundColorHex(backgroundColor);
        }
      }
    }
  };

  // Custom toolbar setup after component mounts - FIXED VERSION
  useEffect(() => {
    const setupCustomToolbar = () => {
      const toolbar = document.querySelector('.ql-toolbar');
      if (toolbar) {
        // Hide default color dropdowns and add custom click handlers
        const textColorPicker = toolbar.querySelector('.ql-color');
        const backgroundColorPicker = toolbar.querySelector('.ql-background');
        
        if (textColorPicker) {
          // Only hide the color picker dropdown, not other dropdowns
          const textDropdown = textColorPicker.querySelector('.ql-picker-options');
          if (textDropdown) textDropdown.style.display = 'none';
          
          // Add click handler to the picker label
          const textColorButton = textColorPicker.querySelector('.ql-picker-label');
          if (textColorButton) {
            // Remove any existing listeners first
            textColorButton.replaceWith(textColorButton.cloneNode(true));
            const newTextColorButton = textColorPicker.querySelector('.ql-picker-label');
            
            newTextColorButton.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowTextColorPicker(!showTextColorPicker);
              setShowBackgroundColorPicker(false);
            });
          }
        }

        if (backgroundColorPicker) {
          // Only hide the background color picker dropdown, not other dropdowns
          const backgroundDropdown = backgroundColorPicker.querySelector('.ql-picker-options');
          if (backgroundDropdown) backgroundDropdown.style.display = 'none';
          
          // Add click handler to the picker label
          const backgroundColorButton = backgroundColorPicker.querySelector('.ql-picker-label');
          if (backgroundColorButton) {
            // Remove any existing listeners first
            backgroundColorButton.replaceWith(backgroundColorButton.cloneNode(true));
            const newBackgroundColorButton = backgroundColorPicker.querySelector('.ql-picker-label');
            
            newBackgroundColorButton.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowBackgroundColorPicker(!showBackgroundColorPicker);
              setShowTextColorPicker(false);
            });
          }
        }
      }
    };

    // Setup toolbar after a short delay to ensure ReactQuill is mounted
    const timer = setTimeout(setupCustomToolbar, 100);
    return () => clearTimeout(timer);
  }, [showTextColorPicker, showBackgroundColorPicker]);

  // Enhanced editor modules with better toolbar
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      ['link', 'blockquote'],
      [{ 'align': [] }],
      ['clean']
    ],
  };

  const formats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'list', 'bullet', 'indent',
    'link', 'blockquote', 'align'
  ];

  // Count words in text (excluding HTML tags)
  const countWords = (html) => {
    const text = html.replace(/<[^>]*>/g, '').trim();
    return text ? text.split(/\s+/).length : 0;
  };

  // Fetch profile content when component mounts
  useEffect(() => {
    try {
      dispatch(getProfile());
    } catch (error) {
      console.error('Error fetching profile content:', error);
    }
  }, [dispatch]);

  // Update text when profile data is loaded
  useEffect(() => {
    if (profile?.about) {
      setText(profile.about);
      setWordCount(countWords(profile.about));
    }
  }, [profile]);

  // Handle text changes
  const handleTextChange = (value) => {
    setText(value);
    setWordCount(countWords(value));
    setHasChanges(value !== (profile?.about || ''));
  };

  // Handle form submission
  const onFinish = async () => {
    try {
      await dispatch(createOrUpdateProfile({ about: text })).unwrap();
      setSuccess(true);
      setFailed(false);
      setHasChanges(false);
      setTimeout(() => {
        setSuccess(false);
      }, 5000);
    } catch (error) {
      console.error('Error updating profile:', error);
      setFailed(true);
      setSuccess(false);
      setTimeout(() => {
        setFailed(false);
      }, 5000);
    }
  };

  const onFinishFailed = () => {
    setFailed(true);
    setSuccess(false);
    setTimeout(() => {
      setFailed(false);
    }, 5000);
  };

  return (
    <div className="max-w-5xl mx-auto p-6 bg-bg-primary">
      {/* Header Section */}
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-4">
          <EditOutlined className="text-2xl text-accent-blue" />
          <Title level={2} className="m-0 text-text-primary font-semibold">
            Edit Profile About Section
          </Title>
        </div>
        <Text className="text-text-secondary text-base">
          Tell your story and showcase your professional background, skills, and experience
        </Text>
      </div>

      {/* Info Alert */}
      <Alert
        className="mb-6 border border-blue-200 bg-blue-50 rounded-lg"
        message="Writing Tips"
        description="Write in first person, highlight your unique value proposition, include relevant keywords for your industry, and keep it engaging and professional."
        type="info"
        showIcon
        icon={<InfoCircleOutlined />}
        closable
      />

      {/* Success/Error Alerts */}
      {success && (
        <Alert
          className="mb-6 border border-accent-green bg-green-50 rounded-lg"
          message="Profile updated successfully!"
          description="Your about section has been saved and is now visible on your profile."
          type="success"
          showIcon
          closable
          onClose={() => setSuccess(false)}
        />
      )}

      {failed && (
        <Alert
          className="mb-6 border border-accent-red bg-red-50 rounded-lg"
          message="Update Failed"
          description="There was an error updating your profile. Please check your content and try again."
          type="error"
          showIcon
          closable
          onClose={() => setFailed(false)}
        />
      )}

      {/* Main Editor Card */}
      <Card 
        className="shadow-medium border border-border-light rounded-xl bg-white"
        bodyStyle={{ padding: '32px' }}
      >
        <Form 
          name="textEditor" 
          onFinish={onFinish} 
          onFinishFailed={onFinishFailed}
          layout="vertical"
        >
          {/* Editor Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <UserOutlined className="text-accent-blue text-lg" />
              <Title level={4} className="m-0 text-text-primary">
                About Me Content
              </Title>
            </div>
            <div className="flex items-center space-x-4">
              <Badge 
                count={wordCount} 
                style={{ backgroundColor: wordCount > 50 ? '#10b981' : '#f97316' }}
                title="Word count"
              />
              {hasChanges && (
                <Badge 
                  status="processing" 
                  text={<Text className="text-text-secondary text-xs">Unsaved changes</Text>}
                />
              )}
            </div>
          </div>

          <Form.Item name="editorContent" className="mb-8">
            <div className="border border-border-light rounded-lg overflow-hidden" style={{ position: 'relative' }}>
              <ReactQuill
                ref={quillRef}
                value={text}
                onChange={handleTextChange}
                modules={modules}
                formats={formats}
                theme="snow"
                placeholder="Tell your professional story... What makes you unique? What are your key achievements and expertise?"
                style={{
                  minHeight: '400px',
                  fontSize: '16px',
                  fontFamily: 'Montserrat, sans-serif'
                }}
              />
              
              {/* Text Color Picker */}
              {showTextColorPicker && (
                <div
                  ref={textColorRef}
                  className="absolute z-50 mt-2 p-3 bg-white border border-gray-300 rounded-lg shadow-lg"
                  style={{ top: '45px', left: '180px' }}
                >
                  <div className="space-y-3">
                    <div className="text-sm font-medium text-gray-700">Text Color</div>
                    <HexColorPicker color={textColor} onChange={handleTextColorChange} />
                    <div className="space-y-2">
                      <label className="text-xs text-gray-600 block">Hex Code:</label>
                      <input
                        type="text"
                        value={textColorHex}
                        onChange={handleTextColorHexChange}
                        onKeyPress={(e) => handleHexKeyPress(e, true)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="#000000"
                        maxLength={7}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Background Color Picker */}
              {showBackgroundColorPicker && (
                <div
                  ref={backgroundColorRef}
                  className="absolute z-50 mt-2 p-3 bg-white border border-gray-300 rounded-lg shadow-lg"
                  style={{ top: '45px', left: '220px' }}
                >
                  <div className="space-y-3">
                    <div className="text-sm font-medium text-gray-700">Background Color</div>
                    <HexColorPicker color={backgroundColor} onChange={handleBackgroundColorChange} />
                    <div className="space-y-2">
                      <label className="text-xs text-gray-600 block">Hex Code:</label>
                      <input
                        type="text"
                        value={backgroundColorHex}
                        onChange={handleBackgroundColorHexChange}
                        onKeyPress={(e) => handleHexKeyPress(e, false)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="#ffff00"
                        maxLength={7}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Form.Item>

          {/* Guidelines Section */}
          <div className="mb-8 p-4 bg-bg-secondary border border-border-light rounded-lg">
            <Title level={5} className="text-text-primary mb-3 flex items-center">
              <CheckCircleOutlined className="mr-2 text-accent-blue" />
              Content Guidelines
            </Title>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <Text className="text-text-primary font-medium block mb-2">✅ Do Include:</Text>
                <ul className="text-text-secondary space-y-1 list-disc list-inside">
                  <li>Your professional background</li>
                  <li>Key skills and expertise</li>
                  <li>Notable achievements</li>
                  <li>Your unique value proposition</li>
                </ul>
              </div>
              <div>
                <Text className="text-text-primary font-medium block mb-2">❌ Avoid:</Text>
                <ul className="text-text-secondary space-y-1 list-disc list-inside">
                  <li>Overly personal information</li>
                  <li>Negative language</li>
                  <li>Irrelevant details</li>
                  <li>Generic descriptions</li>
                </ul>
              </div>
            </div>
          </div>

          <Divider className="border-border-light" />

          {/* Submit Section */}
          <div className="flex justify-between items-center pt-6">
            <div className="flex items-center space-x-4">
              <Text className="text-text-secondary text-sm">
                {wordCount > 0 && (
                  <>
                    <strong>{wordCount}</strong> words • 
                    <span className={wordCount < 50 ? 'text-orange-500' : 'text-green-500'}>
                      {wordCount < 50 ? ' Consider adding more detail' : ' Good length'}
                    </span>
                  </>
                )}
              </Text>
            </div>
            
            <Space>
              <Tooltip title="Save your changes">
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  icon={<SaveOutlined />}
                  loading={loading}
                  disabled={!hasChanges}
                  className="h-12 px-8 bg-accent-blue hover:bg-accent-blue/90 border-accent-blue hover:border-accent-blue/90 rounded-lg font-semibold shadow-soft hover:shadow-medium transition-all duration-200"
                >
                  {loading ? 'Saving...' : 'Save Profile'}
                </Button>
              </Tooltip>
            </Space>
          </div>
        </Form>
      </Card>

    </div>
  );
};

export default ProfileComp;